<template>
  <div
    class="tabulation-print"
    :class="[
      'format-' + printFormat,
      { 'hide-brand-banners': !includeHeaderFooter },
    ]"
  >
    <div class="no-print control-bar">
      <div class="control-bar__left">
        <h4 class="mb-0">Academic Transcript</h4>
        <b-badge variant="light-primary" class="ml-1 text-uppercase">
          Format {{ printFormat }} · {{ formatLabel }}
        </b-badge>
        <b-badge
          v-if="sheetType"
          variant="light-info"
          class="ml-1 text-uppercase"
        >
          {{ typeDisplayName }}
        </b-badge>
      </div>
      <div class="control-bar__actions">
        <div class="print-options no-print">
          <label class="print-options__item">
            <input
              v-model="includeHeaderFooter"
              type="radio"
              :value="true"
            />
            <span>With Header &amp; Footer</span>
          </label>
          <label class="print-options__item">
            <input
              v-model="includeHeaderFooter"
              type="radio"
              :value="false"
            />
            <span>Without Header &amp; Footer</span>
          </label>
        </div>
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-panel">
      <b-spinner variant="primary" style="width: 2.75rem; height: 2.75rem" />
      <h5 class="mt-2 mb-0">Preparing transcript…</h5>
    </div>

    <div v-else-if="error" class="state-panel">
      <feather-icon icon="AlertCircleIcon" size="40" class="text-danger" />
      <h5 class="text-danger mt-2">{{ error }}</h5>
      <button class="btn-modern btn-print mt-2" @click="loadSheetData">
        Retry
      </button>
    </div>

    <div v-else-if="!hasContent" class="state-panel">
      <feather-icon icon="InboxIcon" size="40" class="text-muted" />
      <h5 class="mt-2">No sheet content available</h5>
    </div>

    <!-- â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
         FORMAT 1 â€” Classic Formal (serif Â· double rules)
         â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• -->
    <article v-else-if="printFormat === 1" class="sheet f1">
      <header class="sheet-banner sheet-banner--header">
        <img :src="headerBanner" alt="Happy Palace Group of Schools" />
      </header>
      <div class="sheet-body">
      <div class="sheet-body__inner">
      <div class="student-line">
        <div class="student-line__field">
          <span class="student-line__label">Name:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--cid">
          <span class="student-line__label">CID:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--class">
          <span class="student-line__label">Class:</span>
          <span class="student-line__value" />
        </div>
      </div>
      <div class="rule-thick" />
      <div v-if="isIconBase" class="grade-legend">
        <span>A: Excellent</span>
        <span>B: V. Good</span>
        <span>C: Good</span>
      </div>

      <!-- center content (unchanged structure) -->
      <template v-if="isIconBase">
        <section
          v-for="(category, cIdx) in categories"
          :key="'f1c-' + cIdx"
          class="f1-section"
        >
          <h3 class="f1-cat">
            <span class="f1-cat__num">{{ cIdx + 1 }}</span>
            {{ category.categoryName || "Category" }}
          </h3>
          <table class="f1-table">
            <thead>
              <tr>
                <th class="w-sno">#</th>
                <th>Criteria</th>
                <th class="w-abc th-a">A</th>
                <th class="w-abc th-b">B</th>
                <th class="w-abc th-c">C</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(opt, oIdx) in category.options || []"
                :key="'f1o-' + cIdx + '-' + oIdx"
              >
                <td class="w-sno">{{ opt.sortIndex || oIdx + 1 }}</td>
                <td>{{ opt.optionName || "â€”" }}</td>
                <td class="w-abc"><i class="box" /></td>
                <td class="w-abc"><i class="box" /></td>
                <td class="w-abc"><i class="box" /></td>
              </tr>
            </tbody>
          </table>
        </section>
      </template>
      <template v-else>
        <table class="f1-table">
          <thead>
            <tr>
              <th class="w-sno">#</th>
              <th>{{ isQuestionsBase ? "Question" : "Test" }}</th>
              <th class="w-marks">Marks</th>
              <th class="w-obt">Obtained</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rIdx) in records" :key="'f1r-' + rIdx">
              <td class="w-sno">{{ rIdx + 1 }}</td>
              <td>{{ itemLabel(row, rIdx) }}</td>
              <td class="w-marks">{{ row.totalMarks || "â€”" }}</td>
              <td class="w-obt"><i class="blank" /></td>
            </tr>
          </tbody>
        </table>
      </template>

      <footer class="transcript-footer">
        <div class="sign-row">
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Teacher</p>
          </div>
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Principal</p>
          </div>
        </div>
      </footer>
      </div>
      </div>
      <div class="sheet-banner sheet-banner--footer">
        <img :src="footerBanner" alt="" />
      </div>
    </article>

    <!-- FORMAT 2 -->
    <article v-else-if="printFormat === 2" class="sheet f2">
      <header class="sheet-banner sheet-banner--header">
        <img :src="headerBanner" alt="Happy Palace Group of Schools" />
      </header>
      <div class="sheet-body">
      <div class="sheet-body__inner">
      <div class="student-line">
        <div class="student-line__field">
          <span class="student-line__label">Name:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--cid">
          <span class="student-line__label">CID:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--class">
          <span class="student-line__label">Class:</span>
          <span class="student-line__value" />
        </div>
      </div>
      <div class="rule-thick" />
      <div v-if="isIconBase" class="grade-legend">
        <span>A: Excellent</span>
        <span>B: V. Good</span>
        <span>C: Good</span>
      </div>

      <template v-if="isIconBase">
        <section
          v-for="(category, cIdx) in categories"
          :key="'f2c-' + cIdx"
          class="f2-card"
        >
          <div class="f2-card__head">
            <span class="f2-card__index">{{ pad(cIdx + 1) }}</span>
            <h3>{{ category.categoryName || "Category" }}</h3>
            <span class="f2-card__count"
              >{{ (category.options || []).length }} items</span
            >
          </div>
          <ul class="f2-list">
            <li
              v-for="(opt, oIdx) in category.options || []"
              :key="'f2o-' + cIdx + '-' + oIdx"
            >
              <span class="f2-list__n">{{ opt.sortIndex || oIdx + 1 }}</span>
              <span class="f2-list__t">{{ opt.optionName || "—" }}</span>
              <span class="f2-list__g">
                <i class="f2-box f2-box--a" />
                <i class="f2-box f2-box--b" />
                <i class="f2-box f2-box--c" />
              </span>
            </li>
          </ul>
        </section>
      </template>
      <template v-else>
        <section class="f2-card">
          <div class="f2-card__head">
            <span class="f2-card__index">01</span>
            <h3>{{ isQuestionsBase ? "Questions" : "Class Tests" }}</h3>
          </div>
          <ul class="f2-list">
            <li v-for="(row, rIdx) in records" :key="'f2r-' + rIdx">
              <span class="f2-list__n">{{ rIdx + 1 }}</span>
              <span class="f2-list__t"
                >{{ itemLabel(row, rIdx) }}
                <small v-if="row.totalMarks">· {{ row.totalMarks }} mk</small>
              </span>
              <span class="f2-obt"><i /></span>
            </li>
          </ul>
        </section>
      </template>

      <footer class="transcript-footer transcript-footer--pad">
        <div class="sign-row">
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Teacher</p>
          </div>
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Principal</p>
          </div>
        </div>
      </footer>
      </div>
      </div>
      <div class="sheet-banner sheet-banner--footer">
        <img :src="footerBanner" alt="" />
      </div>
    </article>

    <!-- FORMAT 3 -->
    <article v-else-if="printFormat === 3" class="sheet f3">
      <header class="sheet-banner sheet-banner--header">
        <img :src="headerBanner" alt="Happy Palace Group of Schools" />
      </header>
      <div class="sheet-body">
      <div class="sheet-body__inner">
      <div class="student-line">
        <div class="student-line__field">
          <span class="student-line__label">Name:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--cid">
          <span class="student-line__label">CID:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--class">
          <span class="student-line__label">Class:</span>
          <span class="student-line__value" />
        </div>
      </div>
      <div class="rule-thick" />
      <div v-if="isIconBase" class="grade-legend">
        <span>A: Excellent</span>
        <span>B: V. Good</span>
        <span>C: Good</span>
      </div>

      <template v-if="isIconBase">
        <section
          v-for="(category, cIdx) in categories"
          :key="'f3c-' + cIdx"
          class="f3-block"
        >
          <div class="f3-ribbon">
            {{ category.categoryName || "Category " + (cIdx + 1) }}
          </div>
          <div class="f3-cols">
            <div
              v-for="(opt, oIdx) in category.options || []"
              :key="'f3o-' + cIdx + '-' + oIdx"
              class="f3-item"
            >
              <span class="f3-item__num">{{ opt.sortIndex || oIdx + 1 }}</span>
              <span class="f3-item__txt">{{ opt.optionName || "—" }}</span>
              <span class="f3-item__marks">
                <i>A</i><i>B</i><i>C</i>
              </span>
            </div>
          </div>
        </section>
      </template>
      <template v-else>
        <div class="f3-cols">
          <div
            v-for="(row, rIdx) in records"
            :key="'f3r-' + rIdx"
            class="f3-item"
          >
            <span class="f3-item__num">{{ rIdx + 1 }}</span>
            <span class="f3-item__txt"
              >{{ itemLabel(row, rIdx) }}
              <small v-if="row.totalMarks">({{ row.totalMarks }})</small>
            </span>
            <span class="f3-item__blank" />
          </div>
        </div>
      </template>

      <footer class="transcript-footer">
        <div class="sign-row">
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Teacher</p>
          </div>
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Principal</p>
          </div>
        </div>
      </footer>
      </div>
      </div>
      <div class="sheet-banner sheet-banner--footer">
        <img :src="footerBanner" alt="" />
      </div>
    </article>

    <!-- FORMAT 4 -->
    <article v-else-if="printFormat === 4" class="sheet f4">
      <header class="sheet-banner sheet-banner--header">
        <img :src="headerBanner" alt="Happy Palace Group of Schools" />
      </header>
      <div class="sheet-body">
      <div class="sheet-body__inner">
      <div class="student-line">
        <div class="student-line__field">
          <span class="student-line__label">Name:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--cid">
          <span class="student-line__label">CID:</span>
          <span class="student-line__value" />
        </div>
        <div class="student-line__field student-line__field--class">
          <span class="student-line__label">Class:</span>
          <span class="student-line__value" />
        </div>
      </div>
      <div class="rule-thick" />
      <div v-if="isIconBase" class="grade-legend">
        <span>A: Excellent</span>
        <span>B: V. Good</span>
        <span>C: Good</span>
      </div>

      <template v-if="isIconBase">
        <section
          v-for="(category, cIdx) in categories"
          :key="'f4c-' + cIdx"
          class="f4-sec"
        >
          <div class="f4-sec__title">
            <span>{{ pad(cIdx + 1) }}</span>
            {{ category.categoryName || "Category" }}
          </div>
          <table class="f4-table">
            <thead>
              <tr>
                <th class="w-sno">S#</th>
                <th>Skill / Option</th>
                <th class="w-abc th-a">A</th>
                <th class="w-abc th-b">B</th>
                <th class="w-abc th-c">C</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(opt, oIdx) in category.options || []"
                :key="'f4o-' + cIdx + '-' + oIdx"
                :class="{ alt: oIdx % 2 }"
              >
                <td class="w-sno">{{ opt.sortIndex || oIdx + 1 }}</td>
                <td>{{ opt.optionName || "—" }}</td>
                <td class="w-abc"><i class="sq" /></td>
                <td class="w-abc"><i class="sq" /></td>
                <td class="w-abc"><i class="sq" /></td>
              </tr>
            </tbody>
          </table>
        </section>
      </template>
      <template v-else>
        <table class="f4-table">
          <thead>
            <tr>
              <th class="w-sno">S#</th>
              <th>{{ isQuestionsBase ? "Question" : "Test Name" }}</th>
              <th class="w-marks">Marks</th>
              <th class="w-obt">Obtained</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rIdx) in records"
              :key="'f4r-' + rIdx"
              :class="{ alt: rIdx % 2 }"
            >
              <td class="w-sno">{{ rIdx + 1 }}</td>
              <td>{{ itemLabel(row, rIdx) }}</td>
              <td class="w-marks">{{ row.totalMarks || "—" }}</td>
              <td class="w-obt"><i class="blank" /></td>
            </tr>
            <tr v-if="isQuestionsBase" class="f4-total">
              <td colspan="2">TOTAL</td>
              <td>{{ totalMarks }}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </template>

      <footer class="transcript-footer transcript-footer--pad">
        <div class="sign-row">
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Teacher</p>
          </div>
          <div class="sign-row__block">
            <div class="sign-row__line" />
            <p>Signature of Principal</p>
          </div>
        </div>
      </footer>
      </div>
      </div>
      <div class="sheet-banner sheet-banner--footer">
        <img :src="footerBanner" alt="" />
      </div>
    </article>

    <!-- FORMAT 5 -->
    <template v-else>
      <article
        v-for="copy in 2"
        :key="'f5-' + copy"
        class="sheet f5"
        :class="{ 'page-break': copy > 1 }"
      >
        <div class="f5-frame">
          <div class="f5-frame__inner">
            <header class="sheet-banner sheet-banner--header">
              <img
                :src="headerBanner"
                alt="Happy Palace Group of Schools"
              />
            </header>
            <div class="sheet-body">
            <div class="sheet-body__inner">
            <div class="student-line">
              <div class="student-line__field">
                <span class="student-line__label">Name:</span>
                <span class="student-line__value" />
              </div>
              <div class="student-line__field student-line__field--cid">
                <span class="student-line__label">CID:</span>
                <span class="student-line__value" />
              </div>
              <div class="student-line__field student-line__field--class">
                <span class="student-line__label">Class:</span>
                <span class="student-line__value" />
              </div>
            </div>
            <div class="rule-thick" />
            <div v-if="isIconBase" class="grade-legend">
              <span>A: Excellent</span>
              <span>B: V. Good</span>
              <span>C: Good</span>
            </div>

            <template v-if="isIconBase">
              <section
                v-for="(category, cIdx) in categories"
                :key="'f5c-' + copy + '-' + cIdx"
                class="f5-sec"
              >
                <h3>{{ category.categoryName || "Category" }}</h3>
                <table class="f5-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Criteria</th>
                      <th>A</th>
                      <th>B</th>
                      <th>C</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(opt, oIdx) in category.options || []"
                      :key="'f5o-' + copy + '-' + cIdx + '-' + oIdx"
                    >
                      <td>{{ opt.sortIndex || oIdx + 1 }}</td>
                      <td>{{ opt.optionName || "—" }}</td>
                      <td><i /></td>
                      <td><i /></td>
                      <td><i /></td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </template>
            <template v-else>
              <table class="f5-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ isQuestionsBase ? "Question" : "Test" }}</th>
                    <th>Mk</th>
                    <th>Obt</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, rIdx) in records"
                    :key="'f5r-' + copy + '-' + rIdx"
                  >
                    <td>{{ rIdx + 1 }}</td>
                    <td>{{ itemLabel(row, rIdx) }}</td>
                    <td>{{ row.totalMarks || "—" }}</td>
                    <td><i class="blank" /></td>
                  </tr>
                </tbody>
              </table>
            </template>

            <footer class="transcript-footer transcript-footer--compact">
              <div class="sign-row">
                <div class="sign-row__block">
                  <div class="sign-row__line" />
                  <p>Signature of Teacher</p>
                </div>
                <div class="sign-row__block">
                  <div class="sign-row__line" />
                  <p>Signature of Principal</p>
                </div>
              </div>
            </footer>
            </div>
            </div>
            <div class="sheet-banner sheet-banner--footer">
              <img :src="footerBanner" alt="" />
            </div>
          </div>
        </div>
      </article>
    </template>
  </div>
</template>

<script>
import { BSpinner, BBadge } from "bootstrap-vue";
import { mapActions } from "vuex";
import axios from "axios";

export default {
  name: "TabulationPrint",
  components: { BSpinner, BBadge },
  data() {
    return {
      loading: false,
      error: null,
      sheetType: "",
      categories: [],
      records: [],
      sheetMeta: {
        className: "",
        sectionName: "",
        subjectName: "",
        examTypeName: "",
        sessionName: "",
      },
      schoolLogo: null,
      campusName: "",
      headerBanner:
        "https://cdn.cloudious.net/file-1790279240275-997451809.png",
      footerBanner:
        "https://cdn.cloudious.net/file-1790279240030-876963732.png",
      includeHeaderFooter: true,
    };
  },
  computed: {
    printFormat() {
      const n = parseInt(this.$route.query.format, 10);
      return n >= 1 && n <= 5 ? n : 1;
    },
    formatLabel() {
      return (
        {
          1: "Classic Formal",
          2: "Modern Scorecard",
          3: "Twin Checklist",
          4: "Official Ledger",
          5: "Framed Certificate",
        }[this.printFormat] || ""
      );
    },
    reportDomain() {
      return String(this.$store.state.report || "")
        .replace(/_/g, "")
        .toLowerCase();
    },
    isHpgs() {
      return this.reportDomain.includes("hpgs");
    },
    schoolName() {
      if (this.isHpgs) return "HAPPY PALACE GROUP OF SCHOOLS";
      const name =
        this.campusName ||
        this.$route.query.campusName ||
        this.$store.state.branchName ||
        "";
      if (name) return String(name).toUpperCase();
      return "SCHOOL";
    },
    campusInitials() {
      return String(this.schoolName || "S")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },
    schoolLevels() {
      if (this.isHpgs) {
        return "Early Years | Primary Years | Middle Years | Matric | O & A Levels | Intermediate";
      }
      return "Academic Excellence · Character · Leadership";
    },
    isIconBase() {
      return this.sheetType === "icon-base";
    },
    isQuestionsBase() {
      return this.sheetType === "questions-base";
    },
    typeDisplayName() {
      if (this.sheetType === "icon-base") return "Icon Base";
      if (this.sheetType === "questions-base") return "Questions Base";
      if (this.sheetType === "class-test") return "Class Test";
      return this.sheetType || "Sheet";
    },
    classLabel() {
      const cls =
        this.sheetMeta.className || this.$route.query.className || "";
      const sec =
        this.sheetMeta.sectionName || this.$route.query.sectionName || "";
      if (cls && sec && sec !== "All" && sec !== "0") return `${cls} - ${sec}`;
      return cls || "";
    },
    metaLine() {
      const bits = [];
      const subject =
        this.sheetMeta.subjectName || this.$route.query.subjectName;
      const exam =
        this.sheetMeta.examTypeName || this.$route.query.examTypeName;
      const session =
        this.sheetMeta.sessionName || this.$route.query.sessionName;
      if (subject) bits.push(subject);
      if (exam) bits.push(exam);
      if (session) bits.push(session);
      return bits.join("  Â·  ");
    },
    totalMarks() {
      return this.records.reduce((sum, row) => {
        return sum + (Number(row.totalMarks) || Number(row.marks) || 0);
      }, 0);
    },
    hasContent() {
      if (this.isIconBase) return this.categories.length > 0;
      return this.records.length > 0;
    },
  },
  async created() {
    if (!this.isHpgs) {
      this.error =
        "This Academic Transcript print is only available for Happy Palace (HPGS).";
      return;
    }
    await Promise.all([this.loadCampusInfo(), this.loadSheetData()]);
  },
  methods: {
    ...mapActions(["get"]),
    pad(n) {
      return String(n).padStart(2, "0");
    },
    itemLabel(row, idx) {
      return (
        row.questionNumber ||
        row.question ||
        row.name ||
        row.testName ||
        "Item " + (idx + 1)
      );
    },
    async loadCampusInfo() {
      const db = this.$route.query.db || this.$store.state.userData.db;
      const cID = this.$route.query.cID || this.$store.state.userData.cId;
      const token = this.$store.state.userData.token;

      // Prefer store logo immediately
      this.schoolLogo = this.$store.state.logo || null;
      this.campusName =
        this.$route.query.campusName || this.$store.state.branchName || "";

      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db, cId: cID },
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find(
            (c) => String(c.id) === String(cID)
          );
          if (match) {
            this.campusName = match.name || this.campusName;
            this.schoolLogo = match.logo || this.schoolLogo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => String(b.id) === String(cID)) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            this.campusName;
          this.schoolLogo =
            currentBranch.logo || this.$store.state.logo || this.schoolLogo;
        }
      }
    },
    async loadSheetData() {
      const id = this.$route.query.id || this.$route.params.id;
      if (!id) {
        this.error = "Tabulation sheet id is missing.";
        return;
      }
      this.loading = true;
      this.error = null;
      const q = this.$route.query;
      this.sheetMeta = {
        className: q.className || "",
        sectionName: q.sectionName || "",
        subjectName: q.subjectName || "",
        examTypeName: q.examTypeName || "",
        sessionName: q.sessionName || "",
      };
      try {
        const db = q.db || this.$store.state.userData.db;
        const cID = q.cID || this.$store.state.userData.cId;
        const response = await this.get({
          url:
            this.$store.state.domain +
            "TabulationSheet/LoadSelected?db=" +
            db +
            "&cID=" +
            cID +
            "&id=" +
            id,
          token: this.$store.state.userData.token,
        });
        const data = response.data || response;
        if (!data) {
          this.categories = [];
          this.records = [];
          return;
        }
        this.sheetType = data.type || q.type || "";
        if (this.sheetType === "icon-base") {
          this.categories = (data.categories || []).map((cat) => ({
            ...cat,
            options: Array.isArray(cat.options) ? cat.options : [],
          }));
          this.records = [];
        } else {
          this.records = data.records || [];
          this.categories = [];
        }
        if (!this.sheetMeta.className && data.className)
          this.sheetMeta.className = data.className;
        if (!this.sheetMeta.sectionName && data.sectionName)
          this.sheetMeta.sectionName = data.sectionName;
        if (!this.sheetMeta.subjectName && data.subjectName)
          this.sheetMeta.subjectName = data.subjectName;
        if (!this.sheetMeta.examTypeName && data.examTypeName)
          this.sheetMeta.examTypeName = data.examTypeName;
        if (!this.sheetMeta.sessionName && data.sessionName)
          this.sheetMeta.sessionName = data.sessionName;
      } catch (err) {
        console.error("Failed to load tabulation sheet:", err);
        this.error =
          (err.response &&
            err.response.data &&
            err.response.data.message) ||
          "Failed to load tabulation sheet. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Black/white print-safe palette (B&W printers) */
$ink: #111111;
$brand-red: #c41e1e;
$red: #111111;
$red-soft: #f2f2f2;
$red-mid: #555555;
$red-deep: #2a2a2a;
$border: #c8c8c8;
$muted: #555555;
$paper: #ffffff;

.tabulation-print {
  background: #ececec;
  min-height: 100vh;
  padding: 16px;
  color: $ink;
  font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
}

.control-bar {
  background: #fff;
  padding: 12px 18px;
  margin-bottom: 16px;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid #ddd;
  &__left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    h4 { font-weight: 700; color: $red; }
  }
  &__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
}
.print-options {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 4px;
  &__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 0.82rem;
    font-weight: 600;
    color: $ink;
    cursor: pointer;
    white-space: nowrap;
    input { margin: 0; cursor: pointer; }
  }
}
.btn-modern {
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.btn-print { background: $red; color: #fff; }
.btn-close { background: $red-soft; color: $red; border: 1px solid #ccc; }
.state-panel {
  background: #fff;
  border-radius: 12px;
  padding: 48px;
  text-align: center;
}
.sheet {
  background: $paper;
  width: 8.5in;
  max-width: 8.5in;
  min-height: 11in;
  margin: 0 auto 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.logo-fallback {
  width: 96px;
  height: 96px;
  margin: 0 auto 14px;
  border-radius: 50%;
  border: 2px solid $brand-red;
  color: $brand-red;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.35rem;
  background: #fff5f5;
  &--sm { width: 52px; height: 52px; font-size: 0.9rem; margin: 0; }
}
.w-sno { width: 32px; text-align: center; }
.w-abc { width: 42px; text-align: center; }
.w-marks, .w-obt { width: 70px; text-align: center; }
.w-rem { width: 26%; }
.th-a, .th-b, .th-c { color: $red !important; }

.f1 { padding: 0; position: relative; overflow: visible; }

/* Brand banners — letterhead images */
.sheet-banner {
  margin: 0;
  padding: 0;
  line-height: 0;
  background: transparent;
  flex: 0 0 auto;
  width: 100%;
  text-align: center;
  img {
    display: block;
    width: 100%;
    margin: 0 auto;
    object-fit: contain;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  &--header {
    img {
      height: 1.5in;
      max-height: 1.5in;
      object-position: center top;
    }
    margin-bottom: 8px;
  }
  &--footer {
    margin-top: auto;
    img {
      height: 1.4in;
      max-height: 1.4in;
      object-position: center bottom;
    }
  }
}

/*
 * Letter sheet: header top · body centered · footer bottom
 */
.sheet-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  padding: 10px 0.45in 12px;
  min-height: 0;
  box-sizing: border-box;
}
.sheet-body__inner {
  width: 100%;
}

.hide-brand-banners {
  .sheet-banner img {
    visibility: hidden;
    opacity: 0;
  }
}

.student-line {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin: 0;
  padding: 0 0 5px;
  width: 100%;
}
.student-line__field {
  flex: 1.6;
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  &--cid { flex: 0.9; }
  &--class { flex: 0.85; }
}
.student-line__label {
  font-weight: 700;
  font-size: 10.5pt;
  white-space: nowrap;
  color: #000;
}
.student-line__value {
  flex: 1;
  border-bottom: 1.5px solid #000;
  min-height: 1.2em;
  font-size: 10.5pt;
  font-weight: 600;
  padding: 0 2px 2px;
}
.rule-thick {
  height: 2.5px;
  background: #000;
  width: 100%;
  margin: 0 0 6px;
}

.transcript-footer {
  margin-top: 12px;
  width: 100%;
  &--pad { padding-bottom: 2px; }
  &--compact { margin-top: 10px; }
}
.grade-legend {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2px 8px;
  margin: 0 0 2px;
  font-size: 10pt;
  font-weight: 700;
  color: #000;
  letter-spacing: 0.01em;
  width: 100%;
}
.sign-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 60px;
  margin-top: 6px;
  padding: 0 4px;
  width: 100%;
}
.sign-row__block {
  width: 190px;
  max-width: 42%;
  text-align: center;
  p {
    margin: 6px 0 0;
    font-size: 10pt;
    font-style: italic;
    font-weight: 400;
    color: #000;
  }
}
.sign-row__line {
  height: 1.5px;
  background: #000;
  width: 100%;
}

.f1-section { margin-bottom: 7px; width: 100%; }
.f1-cat {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 9.5pt;
  font-weight: 700;
  margin: 0 0 3px;
  display: flex;
  align-items: center;
  gap: 7px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: $ink;
}
.f1-cat__num {
  width: 18px;
  height: 18px;
  border: 1.5px solid $ink;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 8pt;
  color: $ink;
  flex-shrink: 0;
}
.f1-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9.5pt;
  th, td {
    border: 1px solid $border;
    padding: 3px 7px;
    line-height: 1.35;
  }
  th {
    background: $red-soft;
    font-weight: 700;
    text-align: left;
    font-size: 9pt;
    letter-spacing: 0.03em;
    color: $ink;
  }
  th.w-abc { text-align: center; }
  .box {
    display: inline-block;
    width: 13px;
    height: 13px;
    border: 1.5px solid $ink;
    font-style: normal;
  }
  .blank {
    display: inline-block;
    width: 44px;
    height: 16px;
    border: 1px solid $ink;
    font-style: normal;
  }
}


.f2 { padding: 0; overflow: visible; }
.f2-card {
  margin: 0 0 14px; border: 1px solid $border;
  border-radius: 12px; overflow: hidden;
}
.f2-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: $red;
  color: #ffffff !important;

  h3,
  span,
  .f2-card__index,
  .f2-card__count {
    color: #ffffff !important;
  }

  h3 {
    flex: 1;
    margin: 0;
    font-size: 10pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}
.f2-card__index {
  font-size: 9pt;
  font-weight: 800;
  opacity: 0.85;
  color: #ffffff !important;
}
.f2-card__count {
  font-size: 8.5pt;
  opacity: 0.9;
  color: #ffffff !important;
}
.f2-list {
  list-style: none; margin: 0; padding: 0; background: #fff;
  li {
    display: flex; align-items: center; gap: 10px;
    padding: 6px 12px; border-bottom: 1px solid $red-soft; font-size: 10pt;
    &:nth-child(even) { background: $red-soft; }
    &:last-child { border-bottom: none; }
  }
}
.f2-list__n {
  width: 22px; height: 22px; border-radius: 6px; background: $red-soft;
  border: 1px solid $border; display: inline-flex; align-items: center;
  justify-content: center; font-size: 0.7rem; font-weight: 700; color: $red; flex-shrink: 0;
}
.f2-list__t { flex: 1; min-width: 0; small { color: $muted; } }
.f2-list__g { display: flex; gap: 6px; }
.f2-box {
  width: 16px; height: 16px; border-radius: 4px;
  border: 2px solid $red; display: inline-block; font-style: normal;
}
.f2-obt i {
  display: inline-block; width: 48px; height: 20px;
  border: 1.5px dashed $red; border-radius: 4px; font-style: normal;
}

.f3 { padding: 0; }
.f3-block { margin-bottom: 12px; }
.f3-ribbon {
  background: $red;
  color: #ffffff !important;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 5px 12px;
  margin-bottom: 6px;
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%);
  max-width: 92%;
}
.f3-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 14px; }
.f3-item {
  display: flex; align-items: center; gap: 6px; padding: 5px 4px;
  border-bottom: 1px dashed $border; font-size: 0.8rem;
}
.f3-item__num { width: 18px; font-weight: 700; color: $red; flex-shrink: 0; }
.f3-item__txt { flex: 1; min-width: 0; small { color: $muted; } }
.f3-item__marks {
  display: flex; gap: 3px; flex-shrink: 0;
  i {
    width: 18px; height: 16px; border: 1px solid $red; border-radius: 2px;
    font-style: normal; font-size: 0.58rem; font-weight: 700;
    display: inline-flex; align-items: center; justify-content: center; color: $red;
  }
}
.f3-item__blank { width: 36px; height: 16px; border: 1px solid $red; flex-shrink: 0; }

.f4 { padding: 0; }
.f4-sec { margin: 0 0 12px; }
.f4-sec__title {
  background: $red;
  color: #ffffff !important;
  font-size: 9.5pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  span {
    opacity: 0.85;
    color: #ffffff !important;
  }
}
.f4-table {
  width: 100%; border-collapse: collapse; font-size: 10pt;
  th, td { border: 1px solid $border; padding: 5px 8px; line-height: 1.4; }
  th {
    background: $red-soft; color: $red; font-weight: 700;
    font-size: 9.5pt; letter-spacing: 0.04em; text-align: left;
  }
  th.w-abc { text-align: center; }
  tr.alt td { background: #f7f7f7; }
  .sq {
    display: inline-block; width: 13px; height: 13px;
    border: 1.5px solid $red; font-style: normal;
  }
  .blank {
    display: inline-block; width: 40px; height: 16px;
    border: 1px solid $red; font-style: normal;
  }
  .f4-total td {
    background: $red-soft; font-weight: 800; text-align: center; color: $red;
  }
}

.f5 { padding: 0; display: flex; flex-direction: column; }
.f5-frame {
  border: 3px double $red;
  padding: 0;
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 11in;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.f5-frame__inner {
  border: 1px solid $border;
  padding: 2mm 3mm;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.f5 .sheet-banner--header {
  margin: 0;
  width: 100%;
}
.f5 .sheet-banner--footer {
  margin-top: auto;
  width: 100%;
}
.f5-sec {
  margin-bottom: 8px;
  h3 {
    font-size: 9.5pt; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.04em; margin: 0 0 3px; color: $red;
    border-bottom: 1px dotted $border; padding-bottom: 2px;
  }
}
.f5-table {
  width: 100%; border-collapse: collapse; font-size: 9.5pt;
  th, td { border: 1px solid $border; padding: 4px 7px; line-height: 1.4; }
  th {
    background: $red-soft; font-weight: 700; text-align: center;
    font-size: 9pt; color: $red;
  }
  th:nth-child(2), td:nth-child(2) { text-align: left; }
  td:first-child, td:nth-child(n + 3) { text-align: center; }
  i {
    display: inline-block; width: 13px; height: 13px;
    border: 1px solid $red; font-style: normal;
  }
  i.blank { width: 28px; height: 12px; }
}
.f5-sign {
  display: flex; justify-content: space-between; margin-top: 16px;
  > div {
    width: 140px; text-align: center;
    .line { height: 1px; background: $red; }
    span { display: block; margin-top: 3px; font-size: 0.65rem; font-style: italic; color: $muted; }
  }
}

@media print {
  .no-print { display: none !important; }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    width: 100% !important;
    height: auto !important;
  }

  .tabulation-print {
    background: #fff !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    height: auto !important;
    overflow: visible !important;
  }

  /*
   * ALL formats 1–5 share this Letter layout:
   *  ┌─ header img (1.5in) ─ fixed top
   *  │  gap
   *  │  ★ centered content band ★
   *  │  gap
   *  └─ footer img (1.4in) ─ fixed bottom
   */
  .sheet,
  .f1, .f2, .f3, .f4, .f5 {
    box-shadow: none !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: 11in !important;
    max-height: none !important;
    overflow: visible !important;
    display: block !important;
    position: relative !important;
    box-sizing: border-box !important;
    page-break-after: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .f5-frame,
  .f5-frame__inner {
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    height: auto !important;
    min-height: 11in !important;
    max-height: none !important;
    overflow: visible !important;
    display: block !important;
    position: relative !important;
  }

  /* ===== HEADER — top of page, ~1.5in image ===== */
  .sheet-banner--header {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    height: 1.55in !important;
    margin: 0 !important;
    padding: 0.12in 0.3in 0.05in !important;
    box-sizing: border-box !important;
    background: #fff !important;
    z-index: 9999 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    line-height: 0 !important;
  }
  .sheet-banner--header img {
    display: block !important;
    width: 100% !important;
    max-width: 7.9in !important;
    height: 1.5in !important;
    max-height: 1.5in !important;
    object-fit: contain !important;
    object-position: center center !important;
    margin: 0 auto !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* ===== FOOTER — bottom of page, ~1.4in image ===== */
  .sheet-banner--footer {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    height: 1.5in !important;
    margin: 0 !important;
    padding: 0.05in 0.3in 0.12in !important;
    box-sizing: border-box !important;
    background: #fff !important;
    z-index: 9999 !important;
    display: flex !important;
    align-items: flex-end !important;
    justify-content: center !important;
    text-align: center !important;
    line-height: 0 !important;
  }
  .sheet-banner--footer img {
    display: block !important;
    width: 100% !important;
    max-width: 7.9in !important;
    height: 1.4in !important;
    max-height: 1.4in !important;
    object-fit: contain !important;
    object-position: center bottom !important;
    margin: 0 auto !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .hide-brand-banners .sheet-banner img {
    visibility: hidden !important;
    opacity: 0 !important;
  }

  /*
   * Content band (all 5 formats, with OR without digital banners):
   * Reserve letterhead zones, vertically center data in the middle.
   * table-cell vertical-align works reliably in Chrome print.
   */
  .sheet-body {
    display: table !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 auto !important;
    padding: 0 !important;
    min-height: 11in !important;
    height: 11in !important;
    max-height: 11in !important;
    box-sizing: border-box !important;
    overflow: visible !important;
    position: static !important;
    border-collapse: collapse !important;
    text-align: left !important;
  }
  .sheet-body__inner {
    display: table-cell !important;
    /* Top-align all formats so bottom signatures never clip under footer zone */
    vertical-align: top !important;
    width: 100% !important;
    /* Top 3in · side margins · bottom clears footer (~1.8in) */
    padding: 3in 0.95in 1.8in !important;
    box-sizing: border-box !important;
    margin: 0 !important;
    text-align: left !important;
  }

  /* Without Header & Footer — same zones for all formats 1–5 */
  .hide-brand-banners .sheet-body__inner {
    padding: 3in 0.95in 1.85in !important;
  }

  .student-line,
  .grade-legend,
  .rule-thick,
  .transcript-footer,
  .f1-section,
  .f1-table,
  .f2-card,
  .f3-block,
  .f4-sec,
  .f5-sec,
  .f4-table,
  .f5-table {
    width: 100% !important;
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    transform: none !important;
  }

  /* Keep format-3 ribbon full-width (was 92% — looked tilted/off-center) */
  .f3-ribbon {
    max-width: 100% !important;
    clip-path: none !important;
  }
  .f3-block,
  .f2-card,
  .f4-sec {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .sheet-banner--header,
  .sheet-banner--footer {
    text-align: center !important;
  }
  .sheet-banner--header img,
  .sheet-banner--footer img {
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .student-line {
    margin: 0 !important;
    padding-bottom: 3px !important;
    gap: 12px !important;
    flex-shrink: 0 !important;
  }
  .student-line__label { font-size: 10pt !important; }
  .student-line__value {
    min-height: 1.1em !important;
    font-size: 10pt !important;
  }
  .rule-thick {
    height: 2px !important;
    margin: 0 0 4px !important;
    flex-shrink: 0 !important;
  }
  .grade-legend {
    font-size: 9.5pt !important;
    padding: 0 0 5px !important;
    margin: 0 0 2px !important;
    flex-shrink: 0 !important;
  }

  /* ===== ALL formats 1–5: compact so content + signatures fit above footer ===== */
  .f1-section { margin-bottom: 3px !important; }
  .f2-card,
  .f3-block,
  .f4-sec,
  .f5-sec { margin-bottom: 2px !important; }

  .f1-cat {
    font-size: 8.5pt !important;
    margin: 0 0 1px !important;
  }
  .f1-cat__num {
    width: 14px !important;
    height: 14px !important;
    font-size: 7pt !important;
  }
  .f1-table,
  .f4-table,
  .f5-table {
    font-size: 8.5pt !important;
  }
  .f1-table th,
  .f1-table td,
  .f4-table th,
  .f4-table td,
  .f5-table th,
  .f5-table td {
    padding: 1px 5px !important;
    line-height: 1.22 !important;
  }
  .f1-table th,
  .f4-table th,
  .f5-table th {
    font-size: 8pt !important;
  }
  .f1-table .box,
  .f4-table .sq,
  .f5-table i {
    width: 11px !important;
    height: 11px !important;
  }
  .w-sno { width: 24px !important; }
  .w-abc { width: 32px !important; }

  /* Format 2 cards */
  .f2-card {
    margin-bottom: 2px !important;
    border-radius: 4px !important;
  }
  .f2-card__head {
    padding: 2px 6px !important;
    gap: 6px !important;
    h3 { font-size: 8pt !important; }
  }
  .f2-card__index { font-size: 7.5pt !important; }
  .f2-card__count { display: none !important; }
  .f2-list li {
    padding: 1px 6px !important;
    font-size: 8pt !important;
    gap: 6px !important;
    line-height: 1.2 !important;
  }


  .f2-list__n {
    width: 14px !important;
    height: 14px !important;
    font-size: 7pt !important;
    border-radius: 3px !important;
  }
  .f2-box {
    width: 10px !important;
    height: 10px !important;
    border-width: 1.5px !important;
    border-radius: 2px !important;
  }

  /* Format 3 twin checklist */
  .f3-ribbon {
    font-size: 8pt !important;
    padding: 2px 6px !important;
    margin-bottom: 2px !important;
    max-width: 100% !important;
    clip-path: none !important;
  }
  .f3-block { margin-bottom: 3px !important; }
  .f3-item {
    padding: 1px 2px !important;
    font-size: 8pt !important;
    gap: 4px !important;
  }
  .f3-cols { gap: 2px 10px !important; }
  .f3-item__marks i {
    width: 14px !important;
    height: 12px !important;
    font-size: 6.5pt !important;
  }

  /* Format 4 ledger */
  .f4-sec { margin-bottom: 3px !important; }
  .f4-sec__title {
    font-size: 8pt !important;
    padding: 2px 6px !important;
    gap: 6px !important;
  }

  /* Format 5 framed */
  .f5-sec {
    margin-bottom: 3px !important;
    h3 {
      font-size: 8pt !important;
      margin: 0 0 1px !important;
      padding-bottom: 1px !important;
    }
  }

  /* Signatures — all formats */
  .transcript-footer,
  .transcript-footer--pad,
  .transcript-footer--compact {
    margin-top: 6px !important;
    padding-bottom: 2px !important;
    flex-shrink: 0 !important;
  }
  .sign-row {
    gap: 40px !important;
    margin-top: 2px !important;
  }
  .sign-row__block {
    width: 160px !important;
    p {
      font-size: 8.5pt !important;
      margin-top: 2px !important;
    }
  }
  .sign-row__line { height: 1px !important; }

  .f1, .f2, .f3, .f4, .f5 { margin-top: 0 !important; }
  .page-break {
    page-break-before: always !important;
    break-before: page !important;
  }
  .sheet:last-child {
    page-break-after: avoid !important;
    break-after: avoid !important;
  }

  .f1-g, .f2-pill, .f3-sq, .f4-k, .f5-g,
  .f2-card__head,
  .f2-card__head h3,
  .f2-card__head span,
  .f3-ribbon,
  .f4-sec__title,
  .f4-sec__title span {
    color: #ffffff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .f2-card__head, .f3-ribbon, .f4-sec__title,
  .f1-table th, .f4-table th, .f5-table th, .logo-fallback {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  @page {
    size: letter portrait;
    margin: 0;
  }
}



</style>
